import { UserClaims } from "@okta/okta-auth-js";
import { useOktaAuth } from "@okta/okta-react";
import React, { useState } from "react";
import { SalesforceApi } from "../api/SalesforceApi";
import { UserApi } from "../api/UserApi";
import { UserRole } from "../lib/models/enums";
import { SFAccount } from "../lib/models/sf-account.model";
import { Broker, SFUserProfile } from "../lib/models/sf-user-profile.model";
import { Preferences, UserPreference } from "../lib/types/UserPreference";
import { UserContext } from "../state/user-context";

function UserContextProvider({ children }: { children: any }) {
  const { authState, oktaAuth } = useOktaAuth();
  const [userInfo, setUserInfo] = useState<UserClaims | null>(null);
  const [sfUserInfo, setSfUserInfo] = useState<SFUserProfile | null>(null);
  const [sfAccounts, setSfAccounts] = useState<Array<SFAccount>>([]);
  const [preferences, setPreferences] = useState<UserPreference | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);

  const isEmployee = (sfUserInfo && sfUserInfo.role !== "External") ?? false;

  const fetchUserInfo = React.useCallback(() => {
    if (userInfo === null) {
      const oktaInfoPromise = oktaAuth.getUser();

      const userInfoPromise = oktaInfoPromise.then(async (info: UserClaims) => {
        if (info.email) {
          return SalesforceApi.getUserProfile(info.email);
        }
      });

      Promise.all([oktaInfoPromise, userInfoPromise]).then(
        ([userInfo, sfProfileData]) => {
          setUserInfo(userInfo);

          if (!sfProfileData?.data) {
            setUserRole("External");
            return;
          }

          setUserRole(sfProfileData.data.role);

          if (sfProfileData?.data.brokers) {
            sfProfileData.data.brokers = sfProfileData?.data.brokers.sort(
              (a: Broker, b: Broker) => {
                if (a.name < b.name) return -1;
                if (b.name < a.name) return 1;
                return 0;
              }
            );
            setSfUserInfo(sfProfileData.data);
          }

          if (userInfo.email) {
            UserApi.getUserPreferences(userInfo.email).then((res) => {
              const userPrefs = res.data;
              userPrefs &&
                setPreferences({
                  id: userPrefs.id,
                  preferences: JSON.parse(userPrefs.preferences as string),
                });
            });
          }
        }
      );
    }
  }, [userInfo, oktaAuth]);

  const fetchSalesforceAccounts = React.useCallback((email: string) => {
    if (!email) return;

    SalesforceApi.getAccounts(email)
      .then(({ data }) => {
        data && setSfAccounts(data);
      })
      .catch((err) => {
        console.error("Could not load accounts from Salesforce!");
      });
  }, []);

  const savePreferencesHelper = async (incoming: Preferences) => {
    if (preferences?.id) {
      await updateUserPreferences(incoming);
      return;
    }

    await saveUserPreferences(incoming);
  };

  const fetchUserPreferences = async () => {
    if (!userInfo?.email) return;

    const prefResponse = await UserApi.getUserPreferences(userInfo.email);
    const { preferences, id } = prefResponse.data;

    preferences &&
      setPreferences({
        id,
        preferences: JSON.parse(preferences as string),
      });
  };

  const updateUserPreferences = async (incoming: Preferences) => {
    if (!userInfo || !userInfo.email || !preferences) return;

    let updatedPreferences: Preferences = {};

    if (preferences?.preferences) {
      updatedPreferences = { ...preferences.preferences };
    }

    updatedPreferences = { ...updatedPreferences, ...incoming };

    const res = await UserApi.updateUserPreferences({
      id: preferences.id,
      username: userInfo.email,
      preferences: updatedPreferences,
    });

    if (res.data.successful) {
      await fetchUserPreferences();
    }
  };

  const saveUserPreferences = async (incoming: Preferences) => {
    if (!userInfo || !userInfo.email) {
      return;
    }

    try {
      const res = await UserApi.saveUserPreferences(
        userInfo.email,
        incoming ?? {}
      );

      if (res.data.successful) {
        await fetchUserPreferences();
      }
    } catch (err) {
      console.error("Could not save user preferences!");
    }
  };

  React.useEffect(() => {
    if (!authState || !authState.isAuthenticated) {
      setUserInfo(null);
    } else {
      fetchUserInfo();
    }
  }, [authState, setUserInfo, fetchUserInfo]);

  React.useEffect(() => {
    fetchSalesforceAccounts(sfUserInfo?.email ?? "");
  }, [sfUserInfo, fetchSalesforceAccounts]);

  return (
    <UserContext.Provider
      value={{
        user: userInfo,
        sfProfile: sfUserInfo,
        sfAccounts: sfAccounts,
        exceptionBalance: 0,
        role: userRole,
        isEmployee,
        userPreferences: preferences,
        savePreferencesHelper,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export default UserContextProvider;
